/**
 * libraryIndicatorService.js
 * 
 * NEW SERVICE: Calculates indicators using the technicalindicators library
 * with proper warm-up (up to 1000 bars).
 * 
 * This replaces the AlphaVantage + backfill approach with a single-source solution.
 * Kept separate so you can revert to the old approach if needed.
 */

import {
  calculateIndicator,
  getRecommendedBarCount,
  hasEnoughBars
} from './LibraryIndicatorCalculator';

import { USE_LIBRARY_INDICATORS } from '../../config/indicatorConfig';

// Re-export for backwards compatibility
export { USE_LIBRARY_INDICATORS };

/**
 * Calculate indicator using the library approach
 * 
 * @param {Array} bars - OHLCV bars (should be 200-1000 for accuracy)
 * @param {string} indicatorType - 'SMA', 'EMA', 'RSI', 'MACD', etc.
 * @param {object} params - Indicator parameters
 * @returns {Array} Calculated indicator values with timestamps
 */
export function calculateLibraryIndicator(bars, indicatorType, params) {
  if (!USE_LIBRARY_INDICATORS) {
    console.log('[libraryIndicatorService] Library indicators disabled, falling back to AlphaVantage');
    return null;
  }
  
  if (!bars || bars.length === 0) {
    console.warn('[libraryIndicatorService] No bars provided');
    return [];
  }
  
  const recommended = getRecommendedBarCount(indicatorType, params);
  const sufficient = hasEnoughBars(indicatorType, params, bars.length);
  
  // Note: We silently proceed with available bars even if below recommended count
  // The indicators will still calculate correctly, just with slightly reduced accuracy during warm-up
  
  try {
    const values = calculateIndicator(indicatorType, params, bars);
    return values;
    
  } catch (error) {
    console.error(`[libraryIndicatorService] Error calculating ${indicatorType}:`, error);
    return [];
  }
}

/**
 * Format indicator values for chart display
 * Converts to the same format as AlphaVantage for compatibility
 */
export function formatForChart(indicatorType, values) {
  if (!values || values.length === 0) {
    return [];
  }
  
  // Multi-value indicators need special handling
  const multiValueIndicators = {
    'MACD': ['MACD', 'signal', 'histogram'],
    'BBANDS': ['upper', 'middle', 'lower'],
    'STOCH': ['k', 'd'],
    'ADX': ['adx', 'pdi', 'mdi']
  };
  
  if (multiValueIndicators[indicatorType]) {
    // Already in the right format from calculator
    return values;
  }
  
  // Single-value indicators
  return values.map(v => ({
    time: v.time,
    value: v.value
  }));
}

/**
 * Get indicator display name
 */
export function getIndicatorDisplayName(indicatorType, params) {
  switch (indicatorType) {
    case 'SMA':
      return `SMA(${params.time_period || params.period})`;
    case 'EMA':
      return `EMA(${params.time_period || params.period})`;
    case 'WMA':
      return `WMA(${params.time_period || params.period})`;
    case 'RSI':
      return `RSI(${params.time_period || params.period || 14})`;
    case 'MACD':
      return `MACD(${params.fastperiod || 12},${params.slowperiod || 26},${params.signalperiod || 9})`;
    case 'BBANDS':
      return `BBANDS(${params.time_period || 20},${params.nbdevup || 2})`;
    case 'STOCH':
      return `STOCH(${params.fastkperiod || 14},${params.fastdperiod || 3})`;
    case 'CCI':
      return `CCI(${params.time_period || 20})`;
    case 'ADX':
      return `ADX(${params.time_period || 14})`;
    case 'SAR':
      return 'Parabolic SAR';
    case 'VWAP':
      return 'VWAP';
    default:
      return indicatorType;
  }
}

/**
 * Check if indicator is supported by the library
 */
export function isIndicatorSupported(indicatorType) {
  const supported = [
    'SMA', 'EMA', 'WMA',
    'RSI', 'MACD', 'BBANDS',
    'STOCH', 'CCI', 'ADX', 'SAR', 'VWAP'
  ];
  
  return supported.includes(indicatorType);
}

/**
 * Get debug info about calculation
 */
export function getCalculationDebugInfo(indicatorType, params, barCount) {
  const recommended = getRecommendedBarCount(indicatorType, params);
  const sufficient = hasEnoughBars(indicatorType, params, barCount);
  
  return {
    indicatorType,
    params,
    barCount,
    recommended,
    sufficient,
    percentOfRecommended: ((barCount / recommended) * 100).toFixed(1),
    status: sufficient ? 'READY' : 'LIMITED'
  };
}

